import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/expand';
const HTTP_METHOD = 'get';

export type MakeGetOrderExpandedDeps<ODetail extends Schema.Top> = {
  readonly orderDetail: ODetail;
};

export const makeGetOrderExpanded = <ODetail extends Schema.Top>(
  deps: MakeGetOrderExpandedDeps<ODetail>
) => {
  const GetOrderExpandedError = createBackendApiError({
    domain: 'Order',
    endpoint: 'GetOrderExpanded',
    path: ENDPOINT,
    identifierField: 'tokenValue',
  } as const);

  const getOrderExpandedFetch = (tokenValue: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { tokenValue } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        attributes: {
          'http.method': 'GET',
          'http.url': ENDPOINT,
          'order.token': tokenValue,
        },
      })
    );

  const getOrderExpanded = (tokenValue: string) =>
    getOrderExpandedFetch(tokenValue).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderDetail)(response),
          404: (response) =>
            Effect.fail(new GetOrderExpandedError.NotFoundError({ tokenValue, response })),
          orElse: (response) =>
            Effect.fail(new GetOrderExpandedError.ApiRequestError({ tokenValue, response })),
        })
      ),
      Effect.tap(() =>
        Effect.logDebug(
          `🌐 ✅ [getOrderExpanded] Successfully fetched expanded order for token: ${tokenValue}`
        )
      ),
      Effect.withSpan('api.getOrderExpanded', {
        attributes: {
          'api.operation': 'read',
          'api.entity': 'orderExpanded',
          'order.token': tokenValue,
        },
      })
    );

  return { getOrderExpanded, GetOrderExpandedError };
};
